/**
 * Custom Cursor Configuration
 * 
 * Controls whether the custom cursor is enabled.
 * Set ENABLE_CUSTOM_CURSOR to false to use the default Windows cursor.
 * 
 * Why use a custom cursor?
 * - Discord, OBS, and other streaming software don't capture the Windows cursor
 * - A React-rendered cursor is visible in streams and recordings
 * - The custom cursor doesn't interfere with game mechanics (pointer-events: none)
 *
 * Capacitor / coarse-pointer devices: must stay off — the implementation walks added DOM
 * subtrees via MutationObserver (and a one-time pass on mount); on Android WebView heavy
 * DOM work plus the IME can still stress the app.
 */

import { Capacitor } from '@capacitor/core';

export const ENABLE_CUSTOM_CURSOR = true;

/**
 * Set on `document.documentElement` while battle `TargetingCursor` is active (deploy / attack / target pick).
 * Global `CustomCursor` must suspend — it force-applies `cursor: url(…), auto` everywhere and redraws the OS arrow.
 */
export const TARGETING_CURSOR_SUPPRESS_CLASS = 'targeting-cursor-suppress-system-pointer';

/** While Negara peaceful-win outro is open — CustomCursor renders above the outro layer. */
export const NEGARA_OUTRO_ACTIVE_CLASS = 'eu-negara-outro-active';

/**
 * Body-level z-index stack (portaled previews, custom cursor, ability tooltips).
 * Custom cursor must render above fixed order previews or it disappears behind them.
 */
export const CURSOR_Z_INDEX = {
  fixedOrderPreview: 10000010,
  customCursor: 10000050,
  negaraOutro: 10000070,
  /** Custom cursor must sit above the full-screen outro portal. */
  customCursorAboveNegaraOutro: 10000080,
  abilityTooltip: 10000060,
};

/**
 * @returns {boolean} whether CustomCursor should mount (desktop Electron / fine-pointer web only)
 */
export function shouldEnableCustomCursor() {
  if (!ENABLE_CUSTOM_CURSOR) return false;
  if (typeof window === 'undefined') return false;
  try {
    if (Capacitor.isNativePlatform()) return false;
  } catch {
    /* ignore */
  }
  if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return false;
  return true;
}

/**
 * Cursor appearance settings
 */
export const CURSOR_SETTINGS = {
  // Normal cursor size (px)
  normalSize: 20,
  
  // Pointer cursor size when hovering over clickable elements (px)
  pointerSize: 28,
  
  // Normal cursor color (rgba)
  normalColor: 'rgba(255, 255, 255, 0.9)',
  
  // Pointer cursor color when hovering over clickable elements (rgba)
  pointerColor: 'rgba(255, 215, 0, 0.9)', // Gold
  
  // Use mix-blend-mode for visibility on any background
  useBlendMode: true,
};
